import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { LogoCarousel } from '../components/ui/LogoCarousel';
import { SEO, getOrganizationSchema, getWebPageSchema } from '../components/SEO';
import { 
  Users, BookOpen, Briefcase, Compass, 
  ArrowRight, Globe, Award, Languages,
  Building, MapPin, Clock, ChevronRight,
  UserPlus, GraduationCap, DollarSign, Calculator,
  Target, Cpu, Building2, FileText, Handshake
} from 'lucide-react';

const API = '/api';

const HERO_IMAGE = 'https://images.pexels.com/photos/7108207/pexels-photo-7108207.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
const TRAINING_IMAGE = 'https://images.pexels.com/photos/7647951/pexels-photo-7647951.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
const TEAM_IMAGE = 'https://images.unsplash.com/photo-1758691736975-9f7f643d178e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBkaXZlcnNlJTIwYnVzaW5lc3MlMjB0ZWFtJTIwb2ZmaWNlfGVufDB8fHx8MTc3NDUyOTcxNnww&ixlib=rb-4.1.0&q=85';

// Icon mapping for CMS services
const ICON_MAP = {
  'Users': Users,
  'UserPlus': UserPlus,
  'BookOpen': BookOpen,
  'GraduationCap': GraduationCap,
  'Briefcase': Briefcase,
  'DollarSign': DollarSign,
  'Calculator': Calculator,
  'Target': Target,
  'Cpu': Cpu,
  'Building2': Building2,
  'FileText': FileText,
  'Compass': Compass,
  'Globe': Globe,
};

const getIconComponent = (iconName) => ICON_MAP[iconName] || Briefcase;

export const HomePage = () => {
  const { t, language, direction } = useLanguage();
  const [featuredJobs, setFeaturedJobs] = useState([]);
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [cmsServices, setCmsServices] = useState([]);
  const [cmsClients, setCmsClients] = useState([]);
  const [cmsPartners, setCmsPartners] = useState([]);
  const [pageContent, setPageContent] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [jobsRes, coursesRes, servicesRes, clientsRes, partnersRes, pageContentRes] = await Promise.all([
          fetch(`${API}/jobs/featured?limit=3`),
          fetch(`${API}/courses/featured?limit=3`),
          fetch(`${API}/cms/services`),
          fetch(`${API}/cms/clients`),
          fetch(`${API}/cms/partners`),
          fetch(`${API}/cms/page-content?sections=home_hero,home_why_wedo`)
        ]);
        
        if (jobsRes.ok) {
          const jobs = await jobsRes.json();
          setFeaturedJobs(jobs);
        }
        
        if (coursesRes.ok) {
          const courses = await coursesRes.json();
          setFeaturedCourses(courses);
        }

        if (servicesRes.ok) {
          const services = await servicesRes.json();
          setCmsServices(services);
        }

        if (clientsRes.ok) {
          const clients = await clientsRes.json();
          setCmsClients(clients);
        }

        if (partnersRes.ok) {
          const partners = await partnersRes.json();
          setCmsPartners(partners);
        }

        if (pageContentRes.ok) {
          const content = await pageContentRes.json();
          setPageContent(content);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Helper to get CMS content with fallback to translations
  const getContent = (sectionKey, field, fallbackKey) => {
    const section = pageContent[sectionKey];
    if (section) {
      const content = language === 'ar' ? section.content_ar : section.content_en;
      if (content && content[field]) {
        return content[field];
      }
    }
    return fallbackKey ? t(fallbackKey) : '';
  };

  // Fallback services if CMS is empty (for backward compatibility)
  const fallbackServices = [
    {
      icon: Users,
      title: t('services.recruitment.title'),
      description: t('services.recruitment.description'),
      href: '/services/recruitment',
      cta: t('services.recruitment.cta'),
    },
    {
      icon: BookOpen,
      title: t('services.training.title'),
      description: t('services.training.description'),
      href: '/services/training',
      cta: t('services.training.cta'),
    },
    {
      icon: Briefcase,
      title: t('services.consulting.title'),
      description: t('services.consulting.description'),
      href: '/services/consulting',
      cta: t('services.consulting.cta'),
    },
    {
      icon: Compass,
      title: t('services.career.title'),
      description: t('services.career.description'),
      href: '/services/career-advisory',
      cta: t('services.career.cta'),
    },
  ];

  // Use CMS services if available, otherwise fallback
  const displayServices = cmsServices.length > 0 
    ? cmsServices.slice(0, 8).map(s => ({
        icon: getIconComponent(s.icon),
        title: language === 'ar' && s.title_ar ? s.title_ar : s.title_en,
        description: language === 'ar' && s.short_description_ar ? s.short_description_ar : (s.short_description_en || s.description_en?.slice(0, 120)),
        href: s.link || '/services',
        cta: language === 'ar' ? 'المزيد' : 'Learn More',
      }))
    : fallbackServices;

  // Why WeDo points - use CMS content with fallback to translations
  const whyPoints = [
    {
      icon: Briefcase,
      title: getContent('home_why_wedo', 'point1_title', 'whyWedo.points.expertise.title'),
      description: getContent('home_why_wedo', 'point1_desc', 'whyWedo.points.expertise.description'),
    },
    {
      icon: Globe,
      title: getContent('home_why_wedo', 'point2_title', 'whyWedo.points.global.title'),
      description: getContent('home_why_wedo', 'point2_desc', 'whyWedo.points.global.description'),
    },
    {
      icon: Award,
      title: getContent('home_why_wedo', 'point3_title', 'whyWedo.points.quality.title'),
      description: getContent('home_why_wedo', 'point3_desc', 'whyWedo.points.quality.description'),
    },
    {
      icon: Languages,
      title: getContent('home_why_wedo', 'point4_title', 'whyWedo.points.bilingual.title'),
      description: getContent('home_why_wedo', 'point4_desc', 'whyWedo.points.bilingual.description'),
    },
  ];

  // Combined structured data for homepage
  const homeStructuredData = [
    getOrganizationSchema(),
    getWebPageSchema({
      name: language === 'ar' ? 'ويدو لخدمات الموارد البشرية - الرئيسية' : 'WeDo HR Services - Home',
      description: language === 'ar' 
        ? 'شريككم الاستراتيجي في الموارد البشرية. خدمات التوظيف والتدريب المهني والاستشارات في أوروبا والشرق الأوسط ودول الخليج.'
        : 'Your strategic partner in human resources. Premium recruitment, professional training, and HR consulting services across Europe, Middle East, and GCC.',
      url: '/',
      type: 'WebPage'
    })
  ];

  return (
    <div data-testid="home-page">
      <SEO
        title="HR Services, Recruitment & Professional Training"
        titleAr="خدمات الموارد البشرية والتوظيف والتدريب المهني"
        description="WeDo HR Services - Your strategic partner for recruitment, professional training courses, HR consulting, and career advisory services across Europe, Middle East, GCC, and Africa."
        descriptionAr="ويدو لخدمات الموارد البشرية - شريككم الاستراتيجي في التوظيف والدورات التدريبية المهنية واستشارات الموارد البشرية والإرشاد المهني في أوروبا والشرق الأوسط ودول الخليج وأفريقيا."
        keywords="HR services, recruitment agency, professional training, corporate training, training courses Europe, training courses Middle East, دورات تدريبية, توظيف, موارد بشرية, تدريب مهني, GCC training, Africa recruitment"
        canonicalUrl="/"
        ogImage="https://recruit-train-hub.preview.emergentagent.com/og-home.jpg"
        structuredData={homeStructuredData}
        language={language}
      />
      {/* Hero Section - Clean White with Subtle Brand Accent */}
      <section 
        className="relative min-h-[90vh] flex items-center bg-white"
        data-testid="hero-section"
      >
        {/* Subtle brand accent - geometric shape */}
        <div className="absolute top-0 end-0 w-1/2 h-full bg-gradient-to-b from-[#33AADD]/5 to-transparent pointer-events-none" />
        <div className="absolute bottom-0 start-0 w-96 h-96 bg-[#33AADD]/5 rounded-full blur-3xl pointer-events-none" />
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold text-[#111827] mb-6 ${direction === 'rtl' ? 'leading-[1.4]' : 'leading-tight'}`}>
                {getContent('home_hero', 'title', 'hero.title')}{' '}
                <span className="text-[#33AADD]">{getContent('home_hero', 'title_highlight', 'hero.titleHighlight')}</span>
              </h1>
              <p className="text-lg text-[#6B7280] mb-8 max-w-xl">
                {getContent('home_hero', 'subtitle', 'hero.subtitle')}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/recruitment-request">
                  <Button size="lg" className="btn-primary" data-testid="hero-cta-recruitment">
                    {getContent('home_hero', 'cta1_text', 'hero.cta1')}
                    <ArrowRight className={`ms-2 h-5 w-5 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
                  </Button>
                </Link>
                <Link to="/courses">
                  <Button size="lg" variant="outline" className="btn-secondary" data-testid="hero-cta-courses">
                    {getContent('home_hero', 'cta2_text', 'hero.cta2')}
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden lg:block animate-fade-in animate-delay-200">
              <img 
                src={HERO_IMAGE} 
                alt="Professional team" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-[#F9FAFB]" data-testid="services-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#111827] mb-4">
              {t('services.title')}
            </h2>
            <p className="text-lg text-[#6B7280] max-w-2xl mx-auto">
              {t('services.subtitle')}
            </p>
          </div>
          
          <div className={`grid md:grid-cols-2 ${displayServices.length > 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-4'} gap-6`}>
            {displayServices.map((service, index) => (
              <Card 
                key={index} 
                className="card-hover group"
                data-testid={`service-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-xl bg-[#33AADD]/10 flex items-center justify-center mb-5 group-hover:bg-[#33AADD] transition-colors">
                    <service.icon className="h-7 w-7 text-[#33AADD] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#111827] mb-3">
                    {service.title}
                  </h3>
                  <p className="text-[#6B7280] text-sm mb-5 line-clamp-3">
                    {service.description}
                  </p>
                  <Link 
                    to={service.href}
                    className="inline-flex items-center text-[#33AADD] font-medium text-sm hover:underline"
                  >
                    {service.cta}
                    <ChevronRight className={`ms-1 h-4 w-4 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why WeDo Section */}
      <section className="section-padding" data-testid="why-wedo-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#111827] mb-4">
                {getContent('home_why_wedo', 'title', 'whyWedo.title')}
              </h2>
              <p className="text-lg text-[#6B7280] mb-10">
                {getContent('home_why_wedo', 'subtitle', 'whyWedo.subtitle')}
              </p>
              
              <div className="grid sm:grid-cols-2 gap-6">
                {whyPoints.map((point, index) => (
                  <div key={index} className="flex gap-4" data-testid={`why-point-${index}`}>
                    <div className="w-12 h-12 rounded-lg bg-[#33AADD]/10 flex items-center justify-center flex-shrink-0">
                      <point.icon className="h-6 w-6 text-[#33AADD]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#111827] mb-1">
                        {point.title}
                      </h4>
                      <p className="text-sm text-[#6B7280]">
                        {point.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img 
                src={TEAM_IMAGE} 
                alt="Our team" 
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -start-6 bg-[#33AADD] text-white p-6 rounded-xl shadow-lg">
                <p className="text-3xl font-bold">10+</p>
                <p className="text-sm opacity-90">{t('footer.germany')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Courses Section */}
      {featuredCourses.length > 0 && (
        <section className="section-padding bg-[#F9FAFB]" data-testid="featured-courses-section">
          <div className="container-custom">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#111827] mb-2">
                  {t('training.featured')}
                </h2>
              </div>
              <Link to="/courses">
                <Button variant="outline" className="btn-secondary" data-testid="view-all-courses-btn">
                  {t('training.viewAll')}
                  <ArrowRight className={`ms-2 h-4 w-4 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
                </Button>
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredCourses.map((course, index) => (
                <Card key={course.course_id} className="card-hover overflow-hidden" data-testid={`featured-course-${index}`}>
                  <div className="aspect-video bg-gradient-to-br from-[#33AADD]/20 to-[#33AADD]/5 flex items-center justify-center">
                    <BookOpen className="h-16 w-16 text-[#33AADD]/40" />
                  </div>
                  <CardContent className="p-6">
                    <div className="flex flex-wrap gap-2 mb-3">
                      <Badge variant="secondary" className="text-xs">
                        {course.delivery_mode === 'online' ? t('training.online') : 
                         course.delivery_mode === 'onsite' ? t('training.onsite') : t('training.hybrid')}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {course.duration_days} {t('training.days')}
                      </Badge>
                    </div>
                    <h3 className="text-lg font-semibold text-[#111827] mb-2 line-clamp-2">
                      {language === 'ar' ? course.title_ar : course.title}
                    </h3>
                    <p className="text-sm text-[#6B7280] mb-4 line-clamp-2">
                      {language === 'ar' ? course.description_ar : course.description}
                    </p>
                    <div className="flex items-center justify-between">
                      {course.price ? (
                        <span className="font-semibold text-[#111827]">
                          {course.currency} {course.price}
                        </span>
                      ) : (
                        <span className="text-sm text-[#6B7280]">{t('training.requestQuote')}</span>
                      )}
                      <Link to={`/courses/${course.course_id}`}>
                        <Button size="sm" className="btn-primary">
                          {t('common.learnMore')}
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Jobs Section */}
      {featuredJobs.length > 0 && (
        <section className="section-padding" data-testid="featured-jobs-section">
          <div className="container-custom">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#111827] mb-2">
                  {t('jobs.title')}
                </h2>
              </div>
              <Link to="/jobs">
                <Button variant="outline" className="btn-secondary" data-testid="view-all-jobs-btn">
                  {t('common.seeAll')}
                  <ArrowRight className={`ms-2 h-4 w-4 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
                </Button>
              </Link>
            </div>

            <div className="space-y-4">
              {featuredJobs.map((job, index) => (
                <Card key={job.job_id} className="card-hover" data-testid={`featured-job-${index}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-lg bg-[#33AADD]/10 flex items-center justify-center flex-shrink-0">
                          <Building className="h-6 w-6 text-[#33AADD]" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-[#111827] mb-1">
                            {job.title}
                          </h3>
                          <p className="text-sm text-[#6B7280] mb-2">{job.company}</p>
                          <div className="flex flex-wrap items-center gap-3 text-sm text-[#6B7280]">
                            <span className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {job.location}, {job.country}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {job.employment_type === 'full-time' ? t('jobs.fullTime') :
                               job.employment_type === 'part-time' ? t('jobs.partTime') :
                               job.employment_type === 'contract' ? t('jobs.contract') : t('jobs.internship')}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 ms-auto md:ms-0">
                        <Badge variant="secondary">
                          {job.work_model === 'onsite' ? t('jobs.onsite') :
                           job.work_model === 'hybrid' ? t('jobs.hybrid') : t('jobs.remote')}
                        </Badge>
                        <Link to={`/jobs/${job.job_id}`}>
                          <Button className="btn-primary" data-testid={`apply-job-${index}`}>
                            {t('jobs.apply')}
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Clients Section */}
      {cmsClients.length > 0 && (
        <section className="section-padding bg-[#F9FAFB]" data-testid="clients-section">
          <div className="container-custom">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#111827] mb-4">
                {language === 'ar' ? 'عملاؤنا' : 'Our Clients'}
              </h2>
              <p className="text-lg text-[#6B7280] max-w-2xl mx-auto">
                {language === 'ar' 
                  ? 'نفخر بخدمة مجموعة متنوعة من المؤسسات الرائدة'
                  : 'Proud to serve a diverse range of leading organizations'}
              </p>
            </div>
            
            <LogoCarousel
              items={cmsClients}
              language={language}
              type="client"
              autoScroll={cmsClients.length > 5}
              autoScrollInterval={5000}
            />
          </div>
        </section>
      )}

      {/* Partners Section */}
      {cmsPartners.length > 0 && (
        <section className="section-padding" data-testid="partners-section">
          <div className="container-custom">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#111827] mb-4">
                {language === 'ar' ? 'شركاؤنا' : 'Our Partners'}
              </h2>
              <p className="text-lg text-[#6B7280] max-w-2xl mx-auto">
                {language === 'ar' 
                  ? 'نتعاون مع شركاء استراتيجيين لتقديم حلول متكاملة'
                  : 'Collaborating with strategic partners to deliver integrated solutions'}
              </p>
            </div>
            
            <LogoCarousel
              items={cmsPartners}
              language={language}
              type="partner"
              autoScroll={cmsPartners.length > 5}
              autoScrollInterval={5000}
            />
          </div>
        </section>
      )}

      {/* CTA Section - Recruitment */}
      <section className="py-20 bg-[#33AADD]" data-testid="cta-recruitment-section">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-start">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                {t('cta.recruitment.title')}
              </h2>
              <p className="text-white/90 text-lg max-w-xl">
                {t('cta.recruitment.subtitle')}
              </p>
            </div>
            <Link to="/recruitment-request">
              <Button size="lg" variant="secondary" className="bg-white text-[#33AADD] hover:bg-white/90" data-testid="cta-recruitment-btn">
                {t('cta.recruitment.button')}
                <ArrowRight className={`ms-2 h-5 w-5 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section - Training */}
      <section className="section-padding" data-testid="cta-training-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src={TRAINING_IMAGE} 
                alt="Corporate training" 
                className="rounded-2xl shadow-xl"
              />
            </div>
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#111827] mb-4">
                {t('training.customTraining')}
              </h2>
              <p className="text-lg text-[#6B7280] mb-8">
                {t('training.customTrainingDesc')}
              </p>
              <Link to="/customized-training-request">
                <Button size="lg" className="btn-primary" data-testid="cta-training-btn">
                  {t('training.requestCustom')}
                  <ArrowRight className={`ms-2 h-5 w-5 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
